import React, { useRef, useState } from 'react';
import { Card, Col, Form, Nav, Row, Tab } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import _ from 'lodash';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import {
  breakpointsMediaQuery,
  getColor,
  getPastDates,
  percentageOfAll,
  rgbaColor,
  tooltipFormatter,
  useMediaQuery
} from '@/helpers/utils';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent
]);
import dayjs from 'dayjs';
import { loadLanguages } from 'i18next';
import CustomLegent from '@/app/common/components/charts/CustomLegent';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import AgGridWrapperTracker from '@/app/common/components/ag-grid/AgGridWrapperTracker';
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import Flex from '@/components/common/Flex';
import { useTooltipHide } from '@/hooks/useTooltipHide';

const getOptions = (series, competitors, isMdUp) => ({
  textStyle: {
    fontFamily: 'dana'
  },
  color: [],
  legend: {
    show: false
    // data: ['#1-3', '#4-10', '#11-50', '#51-100', 'بدون رتبه'],
    // left: 5,
    // itemWidth: 10,
    // itemHeight: 10,
    // borderRadius: 0,
    // icon: 'circle',
    // inactiveColor: getColor('gray-400'),
    // textStyle: { color: getColor('gray-700') },
    // itemGap: 20
  },
  xAxis: {
    type: 'category',
    data: competitors,
    axisLine: {
      show: false
    },
    splitLine: {
      lineStyle: {
        color: getColor('gray-200')
      }
    },
    axisTick: {
      show: false
    },
    axisLabel: {
      // rotate: -90, // Rotate the labels by -45 degrees
      margin: 10,
      overflow: 'truncate',
      interval: 0,
      color: getColor('gray-600'),
      formatter: value => _.truncate(value, { length: 8, separator: '' })
    }
  },
  yAxis: {
    type: 'value',
    position: 'left',
    splitLine: {
      lineStyle: {
        color: getColor('gray-200')
      }
    },
    axisLine: {
      show: false
    },
    axisTick: {
      show: false
    },
    axisLabel: {
      padding: [10, 0, 0, 0],
      show: true,
      color: getColor('gray-600'),
      margin: 15
    }
  },
  tooltip: {
    triggerOn: isMdUp ? 'mousemove' : 'click',
    trigger: 'axis',
    padding: [7, 10],
    axisPointer: {
      type: 'none'
    },
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('gray-700') },
    borderWidth: 1,
    transitionDuration: 0,
    formatter: params => {
      let tooltipItem = ``;
      const totalCount = _.sum(params.map(item => item.value));
      params.forEach(el => {
        tooltipItem =
          tooltipItem +
          `
            <div class="text-700 d-flex flex-column flex-md-row gap-0 align-items-start align-items-md-center justify-content-between w-100 gap-1">
            
              <span class="text-800 d-flex flex-row-reverse align-items-center gap-1">${el.seriesName?.toString()?.toPersianDigit()}${el.marker}</span>
              <span class="d-flex align-items-center gap-1">
              <span class="fw-light text-800 fs--1"> (${Number(percentageOfAll(el.value, totalCount)).toFixed(0).toString()?.toPersianDigit()}%)</span>
              <span class="fw-bold text-1000">${el?.value?.toString()?.toPersianDigit()}</span>
              </span>
              </div>`;
      });
      return `<div style="width:${isMdUp && '200px'};" class="d-flex flex-column align-items-end">
              <p class='mb-2 text-1000 fw-bold'>
                ${params[0].axisValue}
              </p>
              ${tooltipItem}
            </div>`;
    }
  },

  series,

  grid: {
    containLabel: true,
    right: '5px',
    left: 0,
    bottom: 0,
    top: 20
  }
});

const NavItem = ({ item }) => {
  return (
    <Nav.Item as="li">
      <Nav.Link
        className="mb-0 navLinkForceColor py-3 cursor-pointer"
        eventKey={item.key.toLowerCase()}
      >
        {item.name}
      </Nav.Link>
    </Nav.Item>
  );
};

function RanksDistCard({ competitors, ranks }) {
  const chartRef = useRef();
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);

  const [navItems] = useState([
    { key: 'chart_view', name: 'توزیع رتبه نمودار' },
    { key: 'table_view', name: 'توزیع رتبه جدول' }
  ]);
  const cardRef = useRef();
  const series = [
    {
      name: '#1-3',
      itemStyle: { color: getColor('primary') },
      type: 'bar',
      stack: 'total',
      data: ranks?.ranks_zero_three
    },
    {
      name: '#4-10',
      itemStyle: { color: rgbaColor(getColor('primary'), 0.8) },
      type: 'bar',
      stack: 'total',
      data: ranks?.ranks_four_then
    },
    {
      name: '#11-50',
      itemStyle: { color: rgbaColor(getColor('primary'), 0.6) },
      type: 'bar',
      stack: 'total',
      data: ranks?.ranks_eleven_fifty
    },
    {
      name: '#51-100',
      itemStyle: { color: rgbaColor(getColor('primary'), 0.4) },

      type: 'bar',
      stack: 'total',
      data: ranks?.ranks_fifty_one_hundred
    },
    {
      name: 'بدون رتبه',
      itemStyle: {
        color: rgbaColor(getColor('primary'), 0.2),
        borderRadius: [5, 5, 0, 0]
      },
      type: 'bar',
      stack: 'total',
      data: ranks?.ranks_above_hundred
    }
  ];
  const [activeTab, setActiveTab] = useState('chart_view');
  return (
    <Card ref={cardRef} className="h-100">
      <Tab.Container
        id="competitors-tab"
        activeKey={activeTab}
        // مشخص کردن تب فعال در صورت وجود پیش‌فرض در پارامتر
        onSelect={e => {
          if (e) {
            setActiveTab(e);
          }
        }}
      >
        {/* <FalconCardHeader
          title={
            <div className="d-flex p-0 align-items-center gap-2">
              توزیع رتبه
              <HelpTooltip html="" />
            </div>
          }
          titleTag="h6"
          className="py-0"
          light
          endEl={
            <div className="d-flex p-0 align-items-center gap-2">
              <Nav
                as="ul"
                className="nav-tabs border-0 flex-nowrap chart-tab tab-active-caret"
              >
                {navItems.map(item => (
                  <NavItem item={item} key={item.key} />
                ))}
              </Nav>
              <CardChartDropDown cardRef={cardRef} chartRef={chartRef} />{' '}
            </div>
          }
        ></FalconCardHeader> */}
        <Card.Header
          as={Flex}
          justifyContent="between"
          alignItems="center"
          className="ps-0 py-0 border-bottom"
        >
          <Nav
            as="ul"
            className="nav-tabs border-0 flex-nowrap chart-tab tab-active-caret"
          >
            {navItems.map(item => (
              <NavItem item={item} key={item.key} />
            ))}
          </Nav>
          {activeTab === 'chart_view' && (
            <CardChartDropDown cardRef={cardRef} chartRef={chartRef} />
          )}
        </Card.Header>
        <Card.Body>
          <Tab.Content>
            <Tab.Pane unmountOnExit eventKey="chart_view">
              <div className="d-flex align-items-center flex-wrap gap-2">
                {series?.map(item => (
                  <CustomLegent
                    key={item.name}
                    name={item.name}
                    chartRef={chartRef}
                    color={item.itemStyle.color}
                  />
                ))}
              </div>
              <div dir="ltr">
                <ReactEChartsCore
                  echarts={echarts}
                  ref={chartRef}
                  option={getOptions(series, competitors, isMdUp)}
                  style={{ height: '21.875rem' }}
                />
              </div>
            </Tab.Pane>
            <Tab.Pane unmountOnExit eventKey="table_view">
              <AgGridWrapper
                height="370px"
                data={[
                  ...competitors.map((item, index) => {
                    const formattedObj = { competitor: item };
                    series.forEach(item => {
                      formattedObj[item.name] = item.data[index];
                    });
                    return formattedObj;
                  })
                ]}
                columns={[
                  {
                    field: 'competitor',
                    type: 'string',
                    title: 'دامنه ها',
                    minWidth: 150,
                    flex: 1
                    // cellRenderer: params => (
                    //   <URLRenderer url={'https://' + params.value} />
                    // )
                  },
                  ...series.map(item => ({
                    field: item.name,
                    type: 'number',
                    title: item.name,
                    width: 103
                  }))
                ]}
              />
            </Tab.Pane>
          </Tab.Content>
        </Card.Body>

        {/* <Card.Footer className="bg-body-tertiary py-2">
        <Row className="g-0 flex-between-center">
          <Col xs="auto">
            <Form.Select size="sm">
              <option>Last 7 days</option>
              <option>Last Month</option>
              <option>Last Year</option>
            </Form.Select>
          </Col>
          <Col xs="auto">
            <FalconLink
              title="Acquisition overview"
              className="px-0 fw-medium"
            />
          </Col>
        </Row>
      </Card.Footer> */}
      </Tab.Container>
    </Card>
  );
}

export default RanksDistCard;
